import { Link, useNavigate } from "react-router-dom";
import { LogOut, Facebook } from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { usePublicPages } from "@/lib/publicPages";

export function Footer() {
  const { t, lp, lang } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const footerPages = usePublicPages().filter((p) => (p.placements || []).includes("footer"));
  const guidesLabel = { pl: "Poradniki", en: "Guides", de: "Ratgeber", es: "Guías" }[lang] || "Guides";
  const widgetLabel = { pl: "Widget na stronę", en: "Website widget", de: "Website-Widget", es: "Widget web", fr: "Widget pour site", it: "Widget per sito", pt: "Widget para site", ru: "Виджет для сайта" }[lang] || "Website widget";
  const installLabel = { pl: "Zainstaluj aplikację", en: "Install the app", de: "App installieren", es: "Instalar la app", fr: "Installer l'app", it: "Installa l'app", pt: "Instalar a app", tr: "Uygulamayı kur", ru: "Установить приложение", ar: "ثبّت التطبيق" }[lang] || "Install the app";
  const a11yLabel = { pl: "Deklaracja dostępności", en: "Accessibility", de: "Barrierefreiheit", es: "Accesibilidad", fr: "Accessibilité", it: "Accessibilità", pt: "Acessibilidade", tr: "Erişilebilirlik", ru: "Доступность", ar: "إمكانية الوصول" }[lang] || "Accessibility";
  return (
    <footer className="border-t border-border bg-secondary">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 md:grid-cols-4 md:px-8">
        <div>
          <div className="flex items-center gap-2">
            <img src="/favicon-96x96.png" alt="freetestnet.com" width="32" height="32" className="h-8 w-8 rounded-lg" />
            <span className="font-head text-lg font-700">Freetestnet<span className="text-primary">.com</span></span>
          </div>
          <p className="mt-3 max-w-xs text-sm text-muted-foreground">{t("footer.tagline")}</p>
          <a
            href="https://facebook.com/freetestnet"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-facebook-link"
            aria-label="Facebook"
            className="mt-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
          >
            <Facebook className="h-5 w-5" />
          </a>
        </div>
        <div>
          <h4 className="text-sm font-600">{t("footer.diagnostics")}</h4>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li><Link to={lp("/test")} className="hover:text-foreground">{t("footer.speed")}</Link></li>
            <li><Link to={lp("/gaming")} className="hover:text-foreground">{t("footer.gamers")}</Link></li>
            <li><Link to={lp("/test")} className="hover:text-foreground">{t("footer.dnsSec")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-600">{t("footer.data")}</h4>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li><Link to={lp("/rankings")} className="hover:text-foreground">{t("footer.ranking")}</Link></li>
            <li><Link to={lp("/isp")} data-testid="footer-isp-link" className="hover:text-foreground">{t("seoPages.hubShort")}</Link></li>
            <li><Link to={lp("/guides")} className="hover:text-foreground" data-testid="footer-guides-link">{guidesLabel}</Link></li>
            <li><Link to={lp("/history")} className="hover:text-foreground">{t("footer.history")}</Link></li>
            <li><Link to={lp("/install")} data-testid="footer-install" className="hover:text-foreground">{installLabel}</Link></li>
            {footerPages.map((p) => (
              <li key={p.slug}><Link to={lp(`/p/${p.slug}`)} data-testid={`footer-page-${p.slug}`} className="hover:text-foreground">{p.title}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-600">{t("footer.user")}</h4>
          {user ? (
            <div className="mt-3" data-testid="footer-user">
              <div className="flex items-center gap-3">
                <UserAvatar user={user} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-600">{user.name || user.email}</div>
                  {user.email && <div className="truncate text-xs text-muted-foreground">{user.email}</div>}
                </div>
              </div>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li><Link to={lp("/history")} className="hover:text-foreground">{t("nav.panel")}</Link></li>
                <li><Link to={lp("/privacy")} className="hover:text-foreground">{t("footer.privacy")}</Link></li>
                <li><Link to={lp("/terms")} className="hover:text-foreground" data-testid="footer-terms-link">{t("footer.terms")}</Link></li>
                <li><Link to={lp("/accessibility")} className="hover:text-foreground" data-testid="footer-a11y-link">{a11yLabel}</Link></li>
                <li>
                  <button data-testid="footer-change-consent" onClick={() => window.dispatchEvent(new Event("ns:open-consent"))} className="hover:text-foreground">
                    {t("footer.changeConsent")}
                  </button>
                </li>
                <li><Link to={lp("/widget")} className="hover:text-foreground" data-testid="footer-widget-link">{widgetLabel}</Link></li>
                <li>
                  <button data-testid="footer-logout" onClick={() => { logout(); nav(lp("/")); }} className="flex items-center gap-1.5 hover:text-foreground">
                    <LogOut className="h-3.5 w-3.5" /> {t("nav.logout")}
                  </button>
                </li>
              </ul>
            </div>
          ) : (
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li><Link to={lp("/register")} className="hover:text-foreground">{t("footer.freeAccount")}</Link></li>
              <li><Link to={lp("/login")} className="hover:text-foreground">{t("footer.login")}</Link></li>
              <li><Link to={lp("/privacy")} className="hover:text-foreground">{t("footer.privacy")}</Link></li>
              <li><Link to={lp("/terms")} className="hover:text-foreground" data-testid="footer-terms-link-guest">{t("footer.terms")}</Link></li>
              <li><Link to={lp("/accessibility")} className="hover:text-foreground" data-testid="footer-a11y-link-guest">{a11yLabel}</Link></li>
              <li>
                <button data-testid="footer-change-consent" onClick={() => window.dispatchEvent(new Event("ns:open-consent"))} className="hover:text-foreground">
                  {t("footer.changeConsent")}
                </button>
              </li>
              <li><Link to={lp("/widget")} className="hover:text-foreground" data-testid="footer-widget-link-guest">{widgetLabel}</Link></li>
            </ul>
          )}
        </div>
      </div>
      <div className="border-t border-border py-4 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} Freetestnet.com. {t("footer.rights")}
      </div>
    </footer>
  );
}
